<script setup>
import { nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

/**
 * A panel anchored under something you clicked.
 *
 * Built rather than layered onto one of the three overlays already here,
 * because each fails a requirement this has:
 *
 *   UI/Dropdown positions itself against its nearest positioned ancestor, so a
 *   card gaining `overflow` or `transform` silently clips or re-parents it.
 *
 *   shell/HubMenu has the keyboard and focus handling — copied here — but is
 *   pinned to its trigger's corner with no anchoring API.
 *
 *   pge-tooltip is the only anchored, teleported one, but it closes on ANY
 *   scroll (a capture-phase listener), which makes a panel anchored to a row in
 *   a scrolling page unusable; it also announces itself as a tooltip, which is
 *   a description of a control, not an interactive surface.
 *
 * Teleported to the body so it escapes the shell's stacking contexts — sidebar
 * 52, top bar 51, drawer 61 — and repositioned, never closed, on scroll.
 */
const props = defineProps({
  open: { type: Boolean, default: false },
  /** The element to sit under. Kept as an element, not a rect, so it re-measures. */
  anchor: { type: [Object, null], default: null },
  offset: { type: Number, default: 8 },
  /**
   * Which edge of the panel meets the anchor. `start` hangs it to the right
   * from the anchor's left edge; `end` hangs it to the left from the anchor's
   * right edge, which is what a control sitting at the end of a row wants —
   * otherwise the panel runs off past the table it belongs to.
   */
  align: { type: String, default: 'start', validator: (v) => ['start', 'end'].includes(v) },
  /**
   * An element to stay horizontally inside, where the viewport is the wrong
   * limit. The week grid is the case this exists for: its Monday column starts
   * at the left edge of the content area, and the hub's sidebar owns the
   * viewport to the left of that — so clamping to the window alone lets a
   * popover anchored to Monday sit on top of the navigation.
   *
   * Defaults to null, which is the viewport clamp every other caller wants.
   */
  boundary: { type: [Object, null], default: null },
  labelledBy: { type: String, default: null },
});

const emit = defineEmits(['update:open']);

const router = useRouter();
const panel = ref(null);
const position = ref({ top: 0, left: 0 });

const GUTTER = 8;

const measure = () => {
  const anchor = props.anchor;
  if (!anchor || !panel.value) return;

  const rect = anchor.getBoundingClientRect();
  const panelRect = panel.value.getBoundingClientRect();

  let top = rect.bottom + window.scrollY + props.offset;
  let left = props.align === 'end'
    ? rect.right + window.scrollX - panelRect.width
    : rect.left + window.scrollX;

  // Clamp horizontally: a row runs the width of the card, so a panel anchored
  // to its left edge can still overhang on a narrow window — and an end-aligned
  // one can run off the other side just as easily.
  //
  // The limits are the viewport's unless a boundary element narrows them, and
  // they are only ever narrowed: a boundary wider than the window would push
  // the panel off screen in the name of staying inside a container.
  const bounds = props.boundary?.getBoundingClientRect?.() ?? null;
  const minX = window.scrollX + Math.max(0, bounds?.left ?? 0);
  const maxX = window.scrollX + Math.min(window.innerWidth, bounds?.right ?? window.innerWidth);

  const maxLeft = maxX - panelRect.width - GUTTER;
  if (left > maxLeft) left = Math.max(minX + GUTTER, maxLeft);
  if (left < minX + GUTTER) left = minX + GUTTER;

  // Flip vertically when there is no room below and more above.
  const below = window.innerHeight - rect.bottom;
  if (below < panelRect.height + props.offset && rect.top > below) {
    top = rect.top + window.scrollY - panelRect.height - props.offset;
  }

  position.value = { top, left };
};

let frame = null;
const scheduleMeasure = () => {
  if (frame) return;
  frame = requestAnimationFrame(() => {
    frame = null;
    measure();
  });
};

const close = ({ restoreFocus = true } = {}) => {
  emit('update:open', false);
  if (restoreFocus) props.anchor?.focus?.();
};

const onKeydown = (event) => {
  if (event.key === 'Escape') {
    event.preventDefault();
    close();
    return;
  }

  if (event.key !== 'Tab' || !panel.value) return;

  // Keep Tab inside the panel while it is open; it is a surface you dismiss,
  // not a stop on the way through the page.
  const focusable = panel.value.querySelectorAll(
    '[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  if (!focusable.length) return;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
};

const onPointerDown = (event) => {
  if (panel.value?.contains(event.target) || props.anchor?.contains?.(event.target)) return;
  close({ restoreFocus: false });
};

/**
 * The layer to draw on: above whatever its anchor sits in. Teleported to the
 * body, the panel stacks against the page, not against its anchor — so opened
 * from inside something drawn high (the session room's panel is at 149 and
 * up), it came up underneath: visible through it, but every click landed on
 * what was on top ("the file's ⋯ menu opens behind the panel"). Its own layer
 * (--xl-z-popover) stands wherever nothing around the anchor is higher.
 */
const layer = ref(null);
let resizeObserver = null;
const POPOVER_LAYER = 70;
const layerAbove = (element) => {
  let highest = 0;
  for (let node = element; node && node !== document.body; node = node.parentElement) {
    const z = Number.parseInt(window.getComputedStyle(node).zIndex, 10);
    if (Number.isFinite(z)) highest = Math.max(highest, z);
  }
  return highest >= POPOVER_LAYER ? highest + 1 : null;
};

watch(
  () => props.open,
  async (isOpen) => {
    if (isOpen) {
      layer.value = props.anchor ? layerAbove(props.anchor) : null;
      await nextTick();
      measure();
      // The panel's first render is at 0,0 — `measure` has set the position but
      // Vue has not written the style yet. A plain focus() therefore scrolls the
      // page to the top to "reveal" it, which on a long list throws the reader
      // somewhere they did not ask to be.
      await nextTick();
      panel.value?.focus({ preventScroll: true });
      // Content that arrives after opening moves the panel with it, rather
      // than leaving it measured for the size it opened at.
      if (typeof ResizeObserver !== 'undefined' && panel.value) {
        resizeObserver = new ResizeObserver(() => scheduleMeasure());
        resizeObserver.observe(panel.value);
      }
      document.addEventListener('mousedown', onPointerDown);
      // Capture, so a scroll in any container repositions rather than strands.
      window.addEventListener('scroll', scheduleMeasure, true);
      window.addEventListener('resize', scheduleMeasure);
    } else {
      resizeObserver?.disconnect();
      resizeObserver = null;
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('scroll', scheduleMeasure, true);
      window.removeEventListener('resize', scheduleMeasure);
    }
  },
  { immediate: true }
);

// A panel that survives navigation hangs over the next page.
const stopAfterEach = router.afterEach(() => {
  if (props.open) close({ restoreFocus: false });
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  document.removeEventListener('mousedown', onPointerDown);
  window.removeEventListener('scroll', scheduleMeasure, true);
  window.removeEventListener('resize', scheduleMeasure);
  if (frame) cancelAnimationFrame(frame);
  stopAfterEach();
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      ref="panel"
      role="dialog"
      aria-modal="false"
      :aria-labelledby="labelledBy"
      tabindex="-1"
      class="xl-popover"
      :style="{ top: `${position.top}px`, left: `${position.left}px`, ...(layer ? { zIndex: layer } : {}) }"
      data-testid="xl-popover"
      @keydown="onKeydown"
    >
      <slot />
    </div>
  </Teleport>
</template>

<style scoped>
.xl-popover, .xl-popover * { box-sizing: border-box; }

.xl-popover {
  position: absolute;
  /* Above the shell's drawer (61); the sidebar is 52 and the top bar 51. */
  z-index: var(--xl-z-popover, 70);
  min-width: 260px;
  max-width: min(360px, calc(100vw - 16px));
  padding: 16px;
  border-radius: var(--xl-radius-lg, 12px);
  border: 1px solid var(--xl-color-border, #e5e0e6);
  background: #fff;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.3), 0 2px 6px 2px rgba(0, 0, 0, 0.15);
}

.xl-popover:focus {
  outline: none;
}
</style>
